import { useEffect, useState } from 'react';
import type { PrologueBeat } from '@plotjack/core';
import { backgroundUrl, titleArtUrl } from '../art/assets';
import { scenePlaceholderSvg } from '../art/placeholders';
import { NarratorPortrait } from './NarratorPortrait';
import { audio } from '../audio/engine';

interface Props {
  beats: PrologueBeat[];
  quest?: string;
  onDone: () => void;
}

function artFor(id: string): string {
  if (id === 'title_keyart') return titleArtUrl() ?? scenePlaceholderSvg('s01_gate', 'calm');
  return backgroundUrl(id, 'calm') ?? scenePlaceholderSvg(id, 'calm');
}

/** Illustrated cold open: who Mira is, what she wants, who is in the way, and who is telling it. */
export function Prologue({ beats, quest, onDone }: Props) {
  const [i, setI] = useState(0);
  const beat = beats[i];
  const last = i >= beats.length - 1;
  const next = () => {
    audio.playSfx('page_turn');
    if (last) onDone();
    else setI((n) => n + 1);
  };

  // the narrator reads each beat aloud when it was baked (scripts/audio/gen.py)
  useEffect(() => {
    if (beat) void audio.playLine(beat.text);
  }, [beat]);
  useEffect(() => () => audio.stopVoice(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      }
      if (e.key === 'Escape') onDone();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!beat) return null;
  return (
    <section className="screen prologue" aria-labelledby="prologue-heading" aria-live="polite">
      <div className="prologue-frame">
        {beats.map((b, n) => (
          <img
            key={b.art + n}
            className={`prologue-art ${n === i ? 'prologue-art--on' : ''}`}
            src={artFor(b.art)}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
        ))}
        <div className="prologue-caption">
          <p className="eyebrow" id="prologue-heading">
            Prologue · {i + 1} of {beats.length}
          </p>
          <div className="prologue-line">
            {beat.narrator && <NarratorPortrait expression={beat.narrator} />}
            <p>{beat.text}</p>
          </div>
          {last && quest && (
            <p className="prologue-quest">
              <span className="quest-label">Your quest</span> {quest}
            </p>
          )}
          <div className="prologue-actions">
            <button type="button" className="btn btn-ghost" onClick={onDone}>
              Skip prologue
            </button>
            <button type="button" className="btn btn-primary" onClick={next} autoFocus>
              {last ? 'Begin Chapter One' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
